// Venues: discovery, bootstrap, listing impressions, saved venues, venue portal.
import {
  apiCall,
  getAPIRoot,
  getAPIBaseURL,
  buildIdentityHeaders,
} from "./core";

export const venueAPI = {
  getAll: async (paramsIn = {}) => {
    const params = new URLSearchParams();

    const {
      city,
      page,
      limit,
      filters = {},
      stag_friendly,
      listing_channel,
    } = paramsIn;

    if (city) params.append("city", String(city).trim());
    if (typeof page === "number") params.append("page", String(page));
    if (typeof limit === "number") params.append("limit", String(limit));
    if (stag_friendly !== undefined)
      params.append("stag_friendly", stag_friendly);
    if (listing_channel)
      params.append("channel", String(listing_channel).trim().toLowerCase());

    if (filters.price && filters.price !== "all") {
      params.append("price", filters.price);
      params.append("price_range_code", filters.price);
    }
    if (filters.type && filters.type !== "all") {
      params.append("type", filters.type);
    }
    if (filters.wifi && filters.wifi !== "all") {
      params.append("wifi", String(filters.wifi));
    }
    if (filters.power && filters.power !== "all") {
      params.append("power", String(filters.power));
    }
    if (filters.vibe && filters.vibe !== "all") {
      params.append("vibe", filters.vibe);
    }
    if (filters.openNow) {
      params.append("open_now", "true");
    }
    if (filters.search) {
      params.append("search", String(filters.search).trim());
    }
    if (filters.stag_friendly) {
      params.append("stag_friendly", "true");
    }
    if (filters.stag_solo_entry) {
      params.append("stag_solo_entry", String(filters.stag_solo_entry));
    }
    if (filters.featured) {
      params.append("featured", "true");
    }
    if (filters.stagster_certified) {
      params.append("stagster_certified", "true");
    }

    const queryString = params.toString();
    const endpoint = `/venues${queryString ? `?${queryString}` : ""}`;

    return await apiCall(endpoint);
  },

  getWorkBootstrap: async (city, options = {}) => {
    if (!city) {
      throw new Error("city is required for work bootstrap");
    }
    const params = new URLSearchParams();
    params.append("city", String(city).trim());
    if (options.fresh) {
      params.append("fresh", "1");
    }
    const endpoint = `/venues/work/bootstrap?${params.toString()}`;
    return await apiCall(endpoint);
  },

  getStagBootstrap: async (city, options = {}) => {
    if (!city) {
      throw new Error("city is required for stag bootstrap");
    }
    const params = new URLSearchParams();
    params.append("city", String(city).trim());
    if (options.fresh) {
      params.append("fresh", "1");
    }
    const endpoint = `/venues/stag/bootstrap?${params.toString()}`;
    return await apiCall(endpoint);
  },

  recordListingImpression: async (venueId, viewerKey) => {
    if (!venueId) throw new Error("venueId is required");
    if (!viewerKey || String(viewerKey).length < 8) {
      throw new Error("viewer_key must be at least 8 characters");
    }
    return await apiCall(`/venues/${venueId}/listing-impressions`, {
      method: "POST",
      body: JSON.stringify({ viewer_key: String(viewerKey) }),
    });
  },

  getById: async (id) => {
    return await apiCall(`/venues/${id}`);
  },

  create: async (venueData) => {
    return await apiCall("/venues", {
      method: "POST",
      body: JSON.stringify(venueData),
    });
  },

  /**
   * Request ownership of an unclaimed venue. Claims are never self-serve —
   * the request lands in the admin action-request queue; approving it binds
   * the claimant as venue owner with portal access.
   */
  claim: async (venueId, note = null) => {
    return await apiCall(`/venues/${venueId}/claim`, {
      method: "POST",
      body: JSON.stringify(note ? { note } : {}),
    });
  },
};

export const venuePulseAPI = {
  get: async (venueId) => apiCall(`/venues/${venueId}/pulse`),
};

export const savedVenuesAPI = {
  getAll: async (userId, token = null) => {
    return await apiCall(`/saved-venues?user_id=${userId}`, {
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  save: async ({
    venueId,
    userId,
    token = null,
    notes = null,
    rating = null,
    reviewText = null,
    isFavorite = false,
  }) => {
    return await apiCall("/saved-venues", {
      method: "POST",
      body: JSON.stringify({
        venue_id: venueId,
        ...(notes !== null ? { notes } : {}),
        ...(rating !== null ? { rating } : {}),
        ...(reviewText !== null ? { review_text: reviewText } : {}),
        ...(typeof isFavorite === "boolean" ? { is_favorite: isFavorite } : {}),
      }),
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  remove: async ({ venueId, userId, token = null }) => {
    return await apiCall(`/saved-venues/${venueId}?user_id=${userId}`, {
      method: "DELETE",
      headers: buildIdentityHeaders({ token, userId }),
    });
  },
};

export const venuePortalAPI = {
  exchangeSession: async () =>
    await apiCall("/venue-portal/session", {
      method: "GET",
      credentials: "include",
    }),

  destroySession: async () => {
    return await apiCall("/venue-portal/session", {
      method: "DELETE",
      credentials: "include",
    });
  },

  getDashboard: async (opts = {}) => {
    return await apiCall("/venue-portal/dashboard", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  getListing: async (opts = {}) => {
    return await apiCall("/venue-portal/listing", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  getFastLaneSettings: async (opts = {}) => {
    return await apiCall("/venue-portal/fastlane/settings", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  updateFastLaneSettings: async (payload) => {
    return await apiCall("/venue-portal/fastlane/settings", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
  },

  getKnownRegulars: async (opts = {}) => {
    return await apiCall("/venue-portal/known-regulars", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  revokeKnownRegular: async (grantId) => {
    return await apiCall(`/venue-portal/known-regulars/${grantId}/revoke`, {
      method: "POST",
      credentials: "include",
    });
  },

  getKnownFaceSettings: async (opts = {}) => {
    return await apiCall("/venue-portal/known-face/settings", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  getDoorTonight: async (opts = {}) => {
    return await apiCall("/venue-portal/door/tonight", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  memberLookup: async (code) => {
    return await apiCall(`/venue-portal/member-lookup?code=${encodeURIComponent(code)}`, {
      method: "GET",
      credentials: "include",
    });
  },

  updateKnownFaceSettings: async (payload) => {
    return await apiCall("/venue-portal/known-face/settings", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
  },

  getInsights: async (opts = {}) => {
    return await apiCall("/venue-portal/insights", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  submitListingRequest: async (payload) => {
    return await apiCall("/venue-portal/listing-request", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
    });
  },

  submitPauseRequest: async (payload) => {
    return await apiCall("/venue-portal/pause-request", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
    });
  },

  submitIssue: async (payload) => {
    return await apiCall("/venue-portal/issue", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
    });
  },

  submitBadgeRequest: async (payload = {}) => {
    return await apiCall("/venue-portal/badge-request", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
    });
  },

  getCampaigns: async (opts = {}) => {
    return await apiCall("/venue-portal/campaigns", {
      method: "GET",
      credentials: "include",
      ...(opts.silent
        ? { suppressErrorLog: true, meta: { silent: true } }
        : {}),
    });
  },

  createCampaign: async (payload) => {
    return await apiCall("/venue-portal/campaigns", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
  },

  adminPreview: async (venueId, token) => {
    if (!token) {
      throw new Error("Admin token required for venue portal preview");
    }
    if (!venueId) {
      throw new Error("venueId is required for admin preview");
    }
    const params = new URLSearchParams({ venue_id: venueId });
    const headers = { Authorization: `Bearer ${token}` };

    const apiRoot = getAPIRoot();
    const preferRelative =
      typeof window !== "undefined" &&
      process.env.NODE_ENV === "production" &&
      getAPIBaseURL()?.includes(".run.app");
    const url = preferRelative
      ? `/api/venue-portal/admin/preview?${params.toString()}`
      : `${apiRoot}/api/venue-portal/admin/preview?${params.toString()}`;

    const response = await fetch(url, { method: "GET", headers });
    const text = await response.text();
    const payload = text
      ? (() => {
          try {
            return JSON.parse(text);
          } catch {
            return text;
          }
        })()
      : null;

    if (!response.ok) {
      const error = new Error(
        (payload && (payload.message || payload.detail)) ||
          `API Error: ${response.status} ${response.statusText}`,
      );
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  },

  adminListRequests: async ({ status, actionType, limit } = {}, token) => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (actionType) params.append("action_type", actionType);
    if (limit != null) params.append("limit", String(limit));
    const qs = params.toString();
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return await apiCall(`/venue-portal/admin/requests${qs ? `?${qs}` : ""}`, {
      method: "GET",
      headers,
      credentials: "include",
    });
  },

  adminResolveRequest: async (requestId, payload, token) => {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return await apiCall(`/venue-portal/admin/requests/${requestId}/resolve`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(payload),
    });
  },

  inviteVenueOwner: async (payload, token) => {
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return await apiCall("/venue-portal/admin/invite", {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(payload),
    });
  },
};
